import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  Upload, Facebook, Image, FileArchive, Check, Loader2,
  AlertCircle, X, Archive
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const EXPORT_TYPES = [
  {
    key: "facebook",
    name: "Facebook",
    icon: Facebook,
    color: "#1877F2",
    instructions: [
      'Go to Facebook Settings → "Your Facebook Information"',
      'Click "Download Your Information"',
      "Select format: JSON, media quality: High",
      "Request download, then upload the ZIP here",
    ],
  },
  {
    key: "instagram",
    name: "Instagram",
    icon: Image,
    color: "#E1306C",
    instructions: [
      'Go to Instagram Settings → "Your Activity"',
      'Click "Download Your Information"',
      "Select JSON format and request download",
      "Upload the ZIP file here when ready",
    ],
  },
  {
    key: "google_photos",
    name: "Google Photos",
    icon: Image,
    color: "#4285F4",
    instructions: [
      "Go to takeout.google.com",
      "Deselect all, then select only Google Photos",
      "Choose .zip format and create export",
      "Upload the ZIP file here when ready",
    ],
  },
];

export default function DataImportPage() {
  const [selectedType, setSelectedType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith(".zip")) {
      toast.error("Please upload a ZIP file");
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      toast.error("File too large. Maximum 500MB.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/import/data-export", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} items from ${res.data.export_type} export!`);
      } else {
        toast.info("No media files found in the archive");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="p-6 md:p-12 max-w-4xl mx-auto" data-testid="data-import-page">
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Import</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Data Import
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Upload your data exports from Facebook, Instagram, or Google Photos
        </p>
      </div>

      {/* Export Type Selection */}
      {!selectedType && !result && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {EXPORT_TYPES.map((type) => (
            <motion.div
              key={type.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => setSelectedType(type)}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-white/15 transition-all cursor-pointer group"
              data-testid={`import-type-${type.key}`}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${type.color}15` }}>
                <type.icon className="w-6 h-6" style={{ color: type.color }} />
              </div>
              <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Manrope' }}>
                {type.name} Export
              </h3>
              <p className="text-xs text-slate-500">Upload your {type.name} data download</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Instructions + Upload */}
      {selectedType && !result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => { setSelectedType(null); setResult(null); }}
              className="text-slate-400 hover:text-white transition-colors text-sm"
            >
              All Exports
            </button>
            <span className="text-slate-600">/</span>
            <span className="text-sm font-medium text-white">{selectedType.name} Import</span>
          </div>

          {/* Instructions */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 mb-6">
            <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
              How to get your {selectedType.name} data
            </h3>
            <ol className="space-y-3">
              {selectedType.instructions.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center text-xs font-bold text-slate-400 shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-slate-300">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Drop Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="drop-zone"
            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-200 ${
              dragActive
                ? "border-[#00FF88] bg-[#00FF88]/5"
                : "border-white/10 bg-white/[0.03] backdrop-blur-xl hover:border-white/20"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="file-input"
            />
            {uploading ? (
              <div>
                <Loader2 className="w-10 h-10 text-[#00FF88] animate-spin mx-auto mb-4" />
                <p className="text-sm text-white font-medium mb-2">
                  {progress < 100 ? `Uploading... ${progress}%` : "Processing archive..."}
                </p>
                <div className="w-64 mx-auto h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#00FF88] rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ) : (
              <div>
                <FileArchive className="w-10 h-10 text-slate-600 mx-auto mb-4" />
                <p className="text-sm text-white font-medium mb-1">
                  Drag & drop your ZIP file here
                </p>
                <p className="text-xs text-slate-500">or click to browse (max 500MB)</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Results */}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="bg-white/[0.03] backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              Import Complete!
            </h2>
            <p className="text-sm text-slate-400">
              Imported <strong className="text-white">{result.imported}</strong> items from your{" "}
              <strong className="text-white capitalize">{result.export_type}</strong> export
              {result.total_found > result.imported && (
                <span> ({result.total_found} found, {result.imported} imported)</span>
              )}
            </p>
          </div>

          {result.entries?.length > 0 && (
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                Imported Items
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {result.entries.map((entry, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.02]">
                    {entry.type === "photo" ? (
                      <Image className="w-4 h-4 text-blue-400" />
                    ) : (
                      <Archive className="w-4 h-4 text-purple-400" />
                    )}
                    <span className="text-sm text-slate-300 truncate">{entry.title}</span>
                    <span className="text-xs text-slate-600 ml-auto capitalize">{entry.type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={() => { setResult(null); setSelectedType(null); }}
              data-testid="import-another-btn"
              className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-6 font-bold"
            >
              Import Another
            </Button>
            <Button
              onClick={() => window.location.href = "/vault"}
              variant="outline"
              className="border-white/10 text-slate-400 rounded-full"
            >
              View Vault
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
